import * as React from 'react'
import { AlertTriangle, Plus, Trash2, Wallet } from 'lucide-react'

import {
  useCancelRegistrationOrder,
  useRegistrationBudget, useRegistrationOrders, useSetRegistrationBudget,
  type RegistrationBudget, type RegistrationOrder, type RegistrationTrack,
} from '@/lib/queries'
import { CreateOrderDialog } from '@/features/registration/CreateOrderDialog'
import { ApiError } from '@/lib/api'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label, Switch } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { cn, formatDateTime } from '@/lib/utils'

function toMajor(minor?: number): string {
  return minor == null ? '' : (minor / 100).toFixed(2)
}

function toMinor(major: string): number | undefined {
  const n = Number(major)
  return major.trim() === '' || Number.isNaN(n) ? undefined : Math.round(n * 100)
}

/**
 * How each order state reads to someone watching their money.
 *
 * 'verifying' is the one that needs explaining rather than a colour: it means a
 * request left the platform and the outcome is genuinely unknown, so nothing
 * will be retried until a check establishes which way it went. Left unexplained
 * it looks like the system is stuck.
 */
const ORDER_STATUS: Record<RegistrationOrder['status'],
  { label: string; variant: 'default' | 'secondary' | 'warning' | 'muted' | 'danger'; hint: string }> = {
  queued: { label: '排队中', variant: 'muted', hint: '等待窗口或额度，尚未尝试下单' },
  registering: { label: '下单中', variant: 'default', hint: '正在向注册商提交注册请求' },
  verifying: {
    label: '结果待确认', variant: 'warning',
    hint: '请求已发出但结果未知（超时或响应异常）。注册商可能已经扣款成功，' +
      '所以不会重试——正在通过可用性复核确认到底成没成，确认前这一单不会再动。',
  },
  succeeded: { label: '注册成功', variant: 'default', hint: '注册商已确认注册完成' },
  failed: { label: '已失败', variant: 'danger', hint: '已达尝试上限或遇到不可恢复的原因' },
  cancelled: { label: '已取消', variant: 'muted', hint: '由你手动取消' },
}

export default function RegistrationPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useRegistrationBudget()
  const { data: orderData } = useRegistrationOrders()
  const setBudget = useSetRegistrationBudget()
  const [creating, setCreating] = React.useState(false)

  const budget = data?.budget
  const providerReady = data?.provider_ready ?? false
  const orders = orderData?.orders ?? []
  const tracks = orderData?.tracks ?? {}

  async function toggle(enabled: boolean) {
    try {
      await setBudget.mutateAsync({ enabled })
      toast.success(enabled ? '自动注册已开启' : '自动注册已关闭')
    } catch (err) {
      toast.error('操作失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <div>
      <PageHeader
        title="自动注册"
        description="域名进入可注册状态时自动下单。这里的每一次动作都会真实扣款。"
        actions={
          <Button size="sm" onClick={() => setCreating(true)} disabled={!budget}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> 新增注册单
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : budget ? (
        <div className="space-y-4">
          {/* The platform-level switch is reported separately from the user's
              own. Without it, someone turns their budget on, nothing happens,
              and there is nothing on screen that explains why. */}
          {!providerReady && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
              <div>
                <p className="font-medium">平台尚未启用注册通道</p>
                <p className="text-muted-foreground">
                  服务端 <code className="text-xs">REGISTRAR_REGISTRATION_ENABLED</code> 为
                  false，或未配置注册商凭据。即使下面开关打开，也不会真正下单。
                </p>
              </div>
            </div>
          )}

          <BudgetPanel budget={budget} onToggle={toggle} pending={setBudget.isPending} />

          <div className="rounded-lg border bg-card">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-sm font-medium">注册单</h2>
              <span className="text-xs text-muted-foreground">每 5 秒自动刷新</span>
            </div>
            {orders.length === 0 ? (
              <EmptyState
                title="还没有注册单"
                description="为某个即将释放的域名建立注册单，它进入可注册状态时会自动下单。"
                icon={Wallet}
              />
            ) : (
              <OrderTable orders={orders} tracks={tracks} />
            )}
          </div>
        </div>
      ) : null}

      {creating && budget && (
        <CreateOrderDialog budget={budget} open onOpenChange={(o) => { if (!o) setCreating(false) }} />
      )}
    </div>
  )
}

function BudgetPanel({
  budget, onToggle, pending,
}: { budget: RegistrationBudget; onToggle: (v: boolean) => void; pending: boolean }) {
  const toast = useToast()
  const setBudget = useSetRegistrationBudget()
  const [daily, setDaily] = React.useState(toMajor(budget.daily_cap_minor))
  const [perOrder, setPerOrder] = React.useState(toMajor(budget.per_order_cap_minor))
  const [concurrent, setConcurrent] = React.useState(String(budget.max_concurrent))

  const remaining = Math.max(0, budget.daily_cap_minor - budget.spent_today_minor)

  async function save() {
    try {
      await setBudget.mutateAsync({
        daily_cap_minor: toMinor(daily),
        per_order_cap_minor: toMinor(perOrder),
        max_concurrent: Number(concurrent) || undefined,
      })
      toast.success('额度已保存')
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <div className={cn('rounded-lg border bg-card', budget.enabled && 'border-primary/40')}>
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h2 className="text-sm font-medium">自动下单总开关</h2>
          <p className="text-xs text-muted-foreground">
            {budget.enabled
              ? '已开启：符合条件的注册单会自动向注册商下单并扣款。'
              : '已关闭：注册单只会排队，不会产生任何支出。'}
          </p>
        </div>
        <Switch checked={budget.enabled} onCheckedChange={onToggle} disabled={pending} aria-label="自动下单总开关" />
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-4">
        <Field label="币种">
          <div className="flex h-9 items-center text-sm tabular-nums">{budget.currency}</div>
        </Field>
        <Field label="每日上限" hint={`今日已用 ${budget.currency} ${toMajor(budget.spent_today_minor)}，剩余 ${toMajor(remaining)}`}>
          <Input type="number" step="0.01" min="0" value={daily} onChange={(e) => setDaily(e.target.value)} className="h-9" />
        </Field>
        <Field label="单笔上限" hint="超过此价的注册单无法建立">
          <Input type="number" step="0.01" min="0" value={perOrder} onChange={(e) => setPerOrder(e.target.value)} className="h-9" />
        </Field>
        <Field label="并发上限" hint="同时在途的注册单数量">
          <Input type="number" min="1" value={concurrent} onChange={(e) => setConcurrent(e.target.value)} className="h-9" />
        </Field>
      </div>

      <div className="flex justify-end border-t px-4 py-3">
        <Button size="sm" variant="outline" onClick={() => void save()} disabled={setBudget.isPending}>
          保存额度
        </Button>
      </div>
    </div>
  )
}

function OrderTable({
  orders, tracks,
}: { orders: RegistrationOrder[]; tracks: Record<string, RegistrationTrack[]> }) {
  const toast = useToast()
  const cancel = useCancelRegistrationOrder()

  return (
    <div className="scroll-x">
      <table className="w-full min-w-[900px] text-sm">
        <caption className="sr-only">自动注册单，共 {orders.length} 条</caption>
        <thead className="border-b bg-muted/40">
          <tr>
            {['域名', '状态', '价格上限', '实际扣款', '年限', '各家进度', '最近动作', '操作'].map((h) => (
              <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const meta = ORDER_STATUS[o.status]
            const cancellable = o.status === 'queued' || o.status === 'registering'
            return (
              <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-3 py-2 font-medium">{o.domain_ascii}</td>
                <td className="px-3 py-2">
                  <Tooltip content={meta.hint}>
                    <Badge variant={meta.variant}>{meta.label}</Badge>
                  </Tooltip>
                </td>
                <td className="px-3 py-2 text-xs tabular-nums">{o.currency} {toMajor(o.max_price_minor)}</td>
                <td className="px-3 py-2 text-xs tabular-nums">
                  {o.charged_minor != null ? `${o.currency} ${toMajor(o.charged_minor)}` : '—'}
                </td>
                <td className="px-3 py-2 text-xs tabular-nums">{o.years} 年</td>
                <td className="px-3 py-2">
                  <TrackList
                    tracks={tracks[o.id] ?? []}
                    maxAttempts={o.max_attempts}
                    settledBy={o.status === 'succeeded' ? o.provider : undefined}
                  />
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {o.last_attempt_at ? formatDateTime(o.last_attempt_at) : '—'}
                  {o.last_error && (
                    <Tooltip content={o.last_error}>
                      <span className="ml-1 cursor-help text-amber-600">·详情</span>
                    </Tooltip>
                  )}
                </td>
                <td className="px-3 py-2">
                  {cancellable ? (
                    <Button
                      variant="ghost" size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      aria-label={`取消 ${o.domain_ascii} 的注册单`}
                      onClick={async () => {
                        try {
                          await cancel.mutateAsync(o.id)
                          toast.success('已取消', o.domain_ascii)
                        } catch (err) {
                          toast.error('取消失败', err instanceof ApiError ? err.message : undefined)
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  ) : (
                    // 'verifying' deliberately has no cancel: its outcome is
                    // unknown, and discarding it would throw away the only
                    // record that a charge may have happened.
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Field({
  label, hint, required, children,
}: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}{required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && <p className="text-[0.7rem] text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * 每一家注册商自己那条线的进度。
 *
 * 一张注册单是几场彼此独立的赛跑，不是一场：每家有自己的次数、自己的节奏、
 * 自己的在途状态。只显示一个汇总数字，就答不了盯掉落时唯一要问的那个问题——
 * 到底是哪家卡住了。
 */
function TrackList({
  tracks, maxAttempts, settledBy,
}: { tracks: RegistrationTrack[]; maxAttempts: number; settledBy?: string }) {
  // A purchase made from the test bench never raced: it went straight to one
  // registrar and either worked or did not. Showing "尚未分配" for it reads as
  // "still waiting", which is the opposite of what happened.
  if (tracks.length === 0 && settledBy) {
    return <Badge variant="default">{settledBy} 直接下单</Badge>
  }
  if (tracks.length === 0) {
    return <span className="text-xs text-muted-foreground">尚未分配</span>
  }
  const meta: Record<RegistrationTrack['status'],
    { label: string; variant: 'default' | 'secondary' | 'warning' | 'muted' | 'danger' }> = {
    queued: { label: '等待', variant: 'muted' },
    registering: { label: '下单中', variant: 'default' },
    verifying: { label: '待确认', variant: 'warning' },
    succeeded: { label: '成功', variant: 'default' },
    failed: { label: '已用尽', variant: 'danger' },
    stopped: { label: '已停止', variant: 'muted' },
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tracks.map((t) => (
        <Tooltip
          key={`${t.provider}#${t.account}`}
          content={
            t.last_error
              ? `${t.provider}/${t.account}：${t.last_error}`
              : `${t.provider}/${t.account}：已下单 ${t.attempts}/${maxAttempts} 次`
          }
        >
          <Badge variant={meta[t.status].variant} className="cursor-help">
            {t.account === 'default' ? t.provider : `${t.provider}·${t.account}`}
            {' '}{meta[t.status].label} {t.attempts}/{maxAttempts}
          </Badge>
        </Tooltip>
      ))}
    </div>
  )
}
